import get from 'lodash/get';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import first from 'lodash/first';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import RxSocket from '@Services/RxSocket';
import SocketBroker from '@Services/SocketBroker';
import UserDatabases from '@Services/UserDatabases';
import RXService from '@Services/RxService';
import SearchStorage from '@Services/SearchStorage';
import { RxSocketUrls, RXTopics, SearchTypes } from '@Constants/CommonConstants';
import Breadcrumb from '@Services/Breadcrumb';
import PriorityWatcher from '@Services/PriorityWatcher';
import BatchExecutionDetails from '@Services/BatchExecutionDetails';
import ResultsInfo from '@Services/ResultsInfo';
import {
  dispatchOpenBatchSearchProgressModal,
  dispatchCloseBatchSearchProgressModal,
} from '@Containers/Search/Shared/BatchSearchProgress/BatchSearchProgressModalActions';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import Deferred from '@Utils/Deferred';

const queryStatuses = {
  ok: 'OK',
  cancelled: 'CANCELED',
};

export class BatchSearchRunnerService {
  _searchId;

  _searchContext;

  _isSearchFinished = false;

  _batchFile;

  _priorityWatcher;

  _fakeGroupId;

  _searchCompletedPromise;

  _hopintoBatchSearch;

  _unregisterErrorsClb;

  _unregisterBatchProgressClb;

  _unsubscribeSockets = () => {
    if (this._unregisterErrorsClb) {
      this._unregisterErrorsClb.call(null);
    }
    if (this._unregisterBatchProgressClb) {
      this._unregisterBatchProgressClb.call(null);
    }
  };

  _reset = () => {
    this._priorityWatcher = new PriorityWatcher({ databaseId: first(UserDatabases.getAll()).id });
    this._isSearchFinished = false;
    this._batchFile = null;
    this._fakeGroupId = new Date().getTime();

    this._unsubscribeSockets();
  };

  /**
   * @ngdoc function
   * @name handleDelayedData
   * @methodOf app.history.controller:HistorySaveCtrl
   * @description We show results page before all databases results have come. So if responses will
   *              keep coming - we need to update results page with these responses data in case if
   *              search is already finished (here search assumed as "finished" once result page
   *              is shown) */
  _handleDelayedData = () => {
    if (this._isSearchFinished) {
      ResultsInfo.delayedResultsInfoSubject.next();
    }
  };

  _batchExecutionStarted = (payload) => {
    const prioritiesItems = map(payload.databases, (databaseId) => {
      return {
        databaseId,
      };
    });
    this._priorityWatcher.init(prioritiesItems, 'databaseId');
    RXService.next(RXTopics.batchSearch.searchStarted, payload.databases);

    ResultsInfo.startLoading(
      map(payload.databases, (databaseId) => {
        return {
          databaseId,
          context: this._searchContext,
          queryId: databaseId, // we have no query id so we're using dbid as unique identifier
          groupId: this._fakeGroupId,
          isFromAdvancedSearch: 1,
        };
      })
    );
  };

  // may be needed for getting progress info by modal controller
  _singleQueryProcessed = (payload) => {
    const result = {
      context: payload.context,
      databaseId: payload.databaseId,
      tempResultsCount: payload.resultSize, // not resultsCount because otherwise
      // getHighPriorityItem will return item before search is finished
      payload,
    };

    this._priorityWatcher.addItem(result, 'databaseId');
    RXService.next(RXTopics.batchSearch.singleQueryProcessed, payload.databaseId);
  };

  _databaseProcessed = (payload) => {
    Logger.info('databaseProcessed', payload);

    const result = {
      context: payload.context,
      databaseId: payload.databaseId,
      resultName: payload.resultName,
      hitSetKey: payload.hitSetKey,
      resultsCount: payload.resultSize,
      payload,
    };

    this._priorityWatcher.addItem(result, 'databaseId');
    RXService.next(RXTopics.batchSearch.databaseProcessed, payload.databaseId);

    const patch = {
      resultsCount: result.resultsCount,
      searchId: this._searchId,
      hitSetKey: result.hitSetKey,
      pageNumber: 1,
      resultName: result.resultName,
      description: null,
      context: payload.context,
      recordCount: payload.recordCount, // Patent Family Clustering
    };

    // we have no query id so we're using dbid as unique identifier
    ResultsInfo.updateInfo(patch, payload.databaseId);
  };

  _showEmptyResultMessage = () => {
    NotifyDialogsService.openModalWindow({
      title: 'No results',
      text: "Reaxys didn't find results for your query. Please consider reformulation and try again.",
      type: 'error',
      confirmText: 'OK',
      backdrop: 'static',
      onConfirm: this._handleBatchHopintoSearch,
      timestamp: new Date().toGMTString(),
      trackingOptions: {
        component: 'Search',
      },
    });
  };

  _batchSearchProcessed = () => {
    // unsubscribe only all data has come
    if (this._priorityWatcher.isAllResultsLoaded()) {
      this._unsubscribeSockets();
    }

    // Show modal If no items for all results
    if (this._priorityWatcher.isAllZeroCount()) {
      this._showEmptyResultMessage();
    }
  };

  /**
   * @ngdoc function
   * @name handleBatchHopintoSearch
   * @description redirects to quick search page if results page can't be displayed during batch search from hopinto */
  _handleBatchHopintoSearch = () => {
    if (this._hopintoBatchSearch) {
      history.push(paths.SEARCH);
    }
  };

  _batchSearchCancelled = () => {
    this._reset();
    this._handleBatchHopintoSearch();
  };

  _showResults = async (result) => {
    this._isSearchFinished = true;
    Breadcrumb.resetBreadcrumbs();
    await SearchStorage.extendItem(this._searchId, {
      searchType: SearchTypes.BATCH_SEARCH,
      batchFileName: this._batchFile.filename,
    })
      .then(() => {
        ResultsInfo.commitResultsToBreadcrumbs(this._fakeGroupId)
          .then(() => {
            if (!result || !result.resultName) {
              return;
            }
            this._searchCompletedPromise.resolve();
            dispatchCloseBatchSearchProgressModal();
            const params = {
              databaseId: result.databaseId,
              context: result.context,
              resultName: result.resultName,
              searchId: this._searchId,
              isFromAdvancedSearch: 1,
              hitSetKey: result.hitSetKey,
            };
            const pathName = generateResultsPath(params);
            history.push({
              pathname: pathName,
              state: params,
            });
          })
          .catch(Logger.error);
      })
      .catch(Logger.error);
  };

  _batchQueryProgressChanged = (data) => {
    // searchId is a number, data.payload.searchId is a string
    if (!data || !data.payload || !this._searchId || this._searchId !== data.payload.searchId) {
      return;
    }

    const { message, payload } = data;

    if (isArray(payload.databases)) {
      this._batchExecutionStarted(payload);
    }

    // eslint-disable-next-line default-case
    switch (message) {
      case 'Batch execution database start': {
        RXService.next(RXTopics.batchSearch.databaseStarted, payload);
        break;
      }
      case 'Batch execution database skip': {
        RXService.next(RXTopics.batchSearch.databaseSkipped, payload);
        break;
      }
      case 'Batch execution single query start': {
        RXService.next(RXTopics.batchSearch.singleQueryStarted, payload);
        break;
      }
      case 'Batch execution single query result': {
        if (!payload.executionMessage) {
          BatchExecutionDetails.addItem(payload);
        } else {
          BatchExecutionDetails.addErrorItem(payload);
        }
        this._singleQueryProcessed(payload);
        break;
      }
      case 'Batch execution database result': {
        BatchExecutionDetails.commit(payload.searchId, payload.databaseId);
        this._databaseProcessed(payload);
        break;
      }
      case 'Batch execution finish': {
        if (payload.status === queryStatuses.ok) {
          this._batchSearchProcessed(payload);
        } else if (payload.status === queryStatuses.cancelled) {
          this._batchSearchCancelled();
          BatchExecutionDetails.clear();
        }
        break;
      }
    }

    this._handleDelayedData();

    const highPriorityResult = this._priorityWatcher.getHighPriorityItemResults();

    if (!this._isSearchFinished && highPriorityResult) {
      this._showResults(highPriorityResult);
    }
  };

  _openProgressInfoModal = () => {
    dispatchOpenBatchSearchProgressModal({
      searchId: this._searchId,
      priorityWatcher: this._priorityWatcher,
      batchFile: this._batchFile,
    });
  };

  _batchQueryError = (error) => {
    const queryId = get(error, 'exception.queryId');
    // searchId is a number, error.exception.queryId is a string
    if (queryId && queryId === this._searchId) {
      dispatchCloseBatchSearchProgressModal();
      this._reset();
    }
  };

  _setupWebSockets = () => {
    this._unregisterErrorsClb = SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, this._batchQueryError);

    this._unregisterBatchProgressClb = SocketBroker.observe(RxSocketUrls.ChannelsUrls.BatchResultsInfo, this._batchQueryProgressChanged);
  };

  sendRequest = (payload, batchFileParam, isHopintoBatchSearch = false) => {
    this._reset();

    this._hopintoBatchSearch = isHopintoBatchSearch;
    this._searchId = payload.searchId.toString();
    this._searchContext = payload.context;
    this._batchFile = batchFileParam;
    this._searchCompletedPromise = Deferred();

    BatchExecutionDetails.clear();
    BatchExecutionDetails.createSearch(this._searchId, payload.context, batchFileParam, payload.options);

    this._openProgressInfoModal();
    this._setupWebSockets();

    RxSocket.send(RxSocketUrls.BatchQuery, payload);
    return this._searchCompletedPromise.promise;
  };
}

const BatchSearchRunner = new BatchSearchRunnerService();
export default BatchSearchRunner;
